import { useRef, useState, useEffect, useCallback, forwardRef } from 'react';
import { Stage, Layer, Rect, Path, Circle, Ellipse, Line, Text, Group } from 'react-konva';
import { useApp } from '@context/AppContext';
import { getStrokePath, flattenPoints } from '@utils/strokeUtils';
import { validateStrokeSize, validateTextContent } from '@utils/validation';
import { getZoneHeight, getZoneBoundaries } from '@/games/corpse/corpseAssignment';
import TextEditPanel from '@components/TextEditPanel';
// floodFill removed — fill now works via shape-based approach

const CANVAS_WIDTH = 2000;
const CANVAS_HEIGHT = 1500;

const SHAPE_TOOLS = new Set(['rect', 'ellipse', 'line', 'bezier']);
const isShapeTool = (tool) => SHAPE_TOOLS.has(tool);

const TOOL_LABELS = {
  pencil: '✏️', brush: '🖌️', marker: '🖍️', pen: '🖊️',
  eraser: '🧹', rect: '▭', ellipse: '◯', line: '╱',
  fill: '🪣', stamp: '🎟️', text: '🔤', select: '👆',
  eyedropper: '💧', bezier: '〰️', transform: '🔄', clipmask: '✂️',
};

const CURSOR_STYLES = {
  select: 'default', eyedropper: 'crosshair', text: 'text',
  transform: 'default', clipmask: 'crosshair',
};

// --- Sub-components for rendering committed elements ---

function ShapeElement({ shape }) {
  const { shapeType, x, y, width, height, colour, fillColour, opacity, size, points,
    offsetX = 0, offsetY = 0, rotation = 0, scaleX = 1, scaleY = 1 } = shape;
  const commonProps = {
    stroke: colour === 'transparent' ? undefined : colour,
    strokeWidth: colour === 'transparent' ? 0 : size,
    opacity,
    fill: fillColour || undefined,
    rotation, scaleX, scaleY,
  };

  // Use a Group to apply offset — this is the same approach as the Path
  // rendering for freehand strokes and works reliably for move/transform.
  if (shapeType === 'rect') {
    return (
      <Group x={offsetX} y={offsetY}>
        <Rect x={x} y={y} width={width} height={height} {...commonProps} />
      </Group>
    );
  }
  if (shapeType === 'ellipse') {
    return (
      <Group x={offsetX} y={offsetY}>
        <Ellipse x={x + width / 2} y={y + height / 2}
          radiusX={Math.abs(width / 2)} radiusY={Math.abs(height / 2)} {...commonProps} />
      </Group>
    );
  }
  if (shapeType === 'line' && points) {
    return (
      <Group x={offsetX} y={offsetY}>
        <Line points={points} {...commonProps} lineCap="round" />
      </Group>
    );
  }
  return null;
}

function ActiveShape({ shape, tool, colour, opacity, size }) {
  if (!shape) return null;
  const { startX, startY, currentX, currentY } = shape;
  const x = Math.min(startX, currentX);
  const y = Math.min(startY, currentY);
  const w = currentX - startX;
  const h = currentY - startY;
  const commonProps = { stroke: colour, strokeWidth: size, opacity, dash: [8, 4] };

  if (tool === 'rect') return <Rect x={x} y={y} width={Math.abs(w)} height={Math.abs(h)} {...commonProps} />;
  if (tool === 'ellipse') {
    return (
      <Ellipse x={startX + w / 2} y={startY + h / 2}
        radiusX={Math.abs(w / 2)} radiusY={Math.abs(h / 2)} {...commonProps} />
    );
  }
  if (tool === 'line') return <Line points={[startX, startY, currentX, currentY]} {...commonProps} lineCap="round" />;
  return null;
}

function renderStroke(stroke, _unused, dimensions, selectedIds) {
  const isSelected = selectedIds?.includes(stroke.id);
  const selectionProps = isSelected ? { shadowColor: '#F59E0B', shadowBlur: 8, shadowOpacity: 0.8 } : {};
  if (stroke.tool === 'shape') return <ShapeElement key={stroke.id} shape={stroke} />;
  if (stroke.tool === 'stamp') {
    const fontSize = Math.max(24, stroke.size * 3);
    return (
      <Text key={stroke.id} text={stroke.stampEmoji}
        x={(stroke.offsetX || 0) + stroke.x - fontSize / 2}
        y={(stroke.offsetY || 0) + stroke.y - fontSize / 2}
        fontSize={fontSize} opacity={stroke.opacity}
        rotation={stroke.rotation || 0} scaleX={stroke.scaleX || 1} scaleY={stroke.scaleY || 1}
        {...selectionProps} />
    );
  }
  if (stroke.tool === 'text') {
    // Parse fontStyle — e.g. "bold italic" or "normal normal"
    const isBold = stroke.fontStyle?.includes('bold');
    const isItalic = stroke.fontStyle?.includes('italic');
    const konvaStyle = [isItalic ? 'italic' : '', isBold ? 'bold' : ''].filter(Boolean).join(' ') || 'normal';
    return (
      <Text key={stroke.id} text={stroke.textContent}
        x={(stroke.offsetX || 0) + stroke.x} y={(stroke.offsetY || 0) + stroke.y}
        fontSize={stroke.fontSize || 24} fontFamily={stroke.fontFamily || 'Space Grotesk'}
        fontStyle={konvaStyle}
        fill={stroke.colour} opacity={stroke.opacity}
        rotation={stroke.rotation || 0} scaleX={stroke.scaleX || 1} scaleY={stroke.scaleY || 1}
        {...selectionProps} />
    );
  }
  if (stroke.tool === 'bezier' && stroke.bezierPoints) {
    return (
      <Line key={stroke.id} points={stroke.bezierPoints}
        stroke={stroke.colour} strokeWidth={stroke.size}
        opacity={stroke.opacity} tension={0.5} lineCap="round"
        {...selectionProps} />
    );
  }
  if (stroke.tool === 'gradient' && stroke.gradientPoints) {
    const [x1, y1, x2, y2] = stroke.gradientPoints;
    const w = Math.abs(x2 - x1) || 1;
    const h = Math.abs(y2 - y1) || 1;
    return (
      <Rect key={stroke.id}
        x={Math.min(x1, x2)} y={Math.min(y1, y2)} width={w} height={h}
        fillLinearGradientStartPoint={{ x: 0, y: 0 }}
        fillLinearGradientEndPoint={{ x: w, y: h }}
        fillLinearGradientColorStops={[0, stroke.colour, 1, stroke.secondaryColour || '#FFFFFF']}
        opacity={stroke.opacity} {...selectionProps} />
    );
  }
  // Freehand strokes (pencil, brush, marker, pen, eraser)
  return (
    <Path key={stroke.id} data={stroke.pathData}
      fill={stroke.tool === 'eraser' ? '#000000' : stroke.colour}
      opacity={stroke.opacity}
      globalCompositeOperation={stroke.tool === 'eraser' ? 'destination-out' : 'source-over'}
      offsetX={-(stroke.offsetX || 0)} offsetY={-(stroke.offsetY || 0)}
      rotation={stroke.rotation || 0} scaleX={stroke.scaleX || 1} scaleY={stroke.scaleY || 1}
      {...selectionProps} />
  );
}

// --- Main component ---

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;

const CanvasStage = forwardRef(function CanvasStage({ onPushStroke, onUpdateCursor, onUpdateStrokeInDB, disabled = false }, stageRef) {
  const { state, dispatch } = useApp();
  const { canvas, cursors, user } = state;
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const isDrawing = useRef(false);
  const pointsRef = useRef([]);
  const shapeStartRef = useRef(null);
  const [activeShape, setActiveShape] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const isPanning = useRef(false);
  const lastPanPoint = useRef(null);

  // Text tool state
  const [textInput, setTextInput] = useState(null); // { x, y } or null
  const [editingStrokeId, setEditingStrokeId] = useState(null); // stroke ID being re-edited

  // Marquee selection state
  const [marquee, setMarqueeState] = useState(null); // { startX, startY, currentX, currentY }
  const marqueeRef = useRef(null);
  const setMarquee = (val) => {
    const resolved = typeof val === 'function' ? val(marqueeRef.current) : val;
    marqueeRef.current = resolved;
    setMarqueeState(resolved);
  };

  // Bezier tool state
  const [bezierPoints, setBezierPoints] = useState([]);

  // Select/move state
  const dragStartRef = useRef(null);
  const selectHitRef = useRef(null); // stroke under pointer at select start
  // Refs for values used in pointer handlers that change frequently.
  // useCallback deps don't include these, so we mirror them in refs
  // to avoid stale closure reads.
  const selectedIdsRef = useRef(canvas.selectedStrokeIds);
  selectedIdsRef.current = canvas.selectedStrokeIds;
  const allStrokesRef = useRef(canvas.allStrokes);
  allStrokesRef.current = canvas.allStrokes;
  const dragOffsetsRef = useRef({});

  // Brush preview state
  const [cursorPos, setCursorPos] = useState(null);

  const baseScale = Math.min(dimensions.width / CANVAS_WIDTH, dimensions.height / CANVAS_HEIGHT);
  const scale = baseScale * zoom;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const getCanvasPoint = useCallback((stage) => {
    const pos = stage.getPointerPosition();
    if (!pos) return null;
    return [(pos.x - pan.x) / scale, (pos.y - pan.y) / scale];
  }, [scale, pan]);

  // --- Zoom/Pan ---
  const handleWheel = useCallback((e) => {
    e.evt.preventDefault();
    const stage = e.target.getStage();
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const direction = e.evt.deltaY > 0 ? -1 : 1;
    const factor = 1.08;
    const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom * (direction > 0 ? factor : 1 / factor)));
    const oldScale = baseScale * zoom;
    const newScale = baseScale * newZoom;
    setPan({
      x: pointer.x - ((pointer.x - pan.x) / oldScale) * newScale,
      y: pointer.y - ((pointer.y - pan.y) / oldScale) * newScale,
    });
    setZoom(newZoom);
  }, [zoom, baseScale, pan]);

  const resetView = useCallback(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, []);
  const zoomIn = useCallback(() => setZoom((z) => Math.min(MAX_ZOOM, z * 1.3)), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(MIN_ZOOM, z / 1.3)), []);

  // --- Touch pinch-to-zoom and two-finger pan ---
  const lastTouchRef = useRef({ dist: 0, center: null });
  const touchCountRef = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const getDistance = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
    const getCenter = (t1, t2) => ({
      x: (t1.clientX + t2.clientX) / 2,
      y: (t1.clientY + t2.clientY) / 2,
    });

    const handleTouchStart = (e) => {
      touchCountRef.current = e.touches.length;
      // Always prevent default on touch start so Android doesn't
      // claim the gesture for scrolling and cancel pointermove events.
      // This is safe because the canvas handles ALL touch interaction.
      e.preventDefault();
      if (e.touches.length >= 2) {
        // Cancel any in-progress drawing
        isDrawing.current = false;
        dispatch({ type: 'SET_ACTIVE_STROKE', payload: null });
        pointsRef.current = [];

        const dist = getDistance(e.touches[0], e.touches[1]);
        const center = getCenter(e.touches[0], e.touches[1]);
        lastTouchRef.current = { dist, center };
      }
    };

    const handleTouchMove = (e) => {
      touchCountRef.current = e.touches.length;
      // Prevent default on all touch moves to stop Android scroll takeover
      e.preventDefault();
      if (e.touches.length >= 2) {
        const dist = getDistance(e.touches[0], e.touches[1]);
        const center = getCenter(e.touches[0], e.touches[1]);
        const prev = lastTouchRef.current;

        // Pinch zoom
        const scaleFactor = dist / prev.dist;
        const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom * scaleFactor));

        // Two-finger pan
        const dx = center.x - prev.center.x;
        const dy = center.y - prev.center.y;

        const oldScale = baseScale * zoom;
        const newScale = baseScale * newZoom;

        setPan((p) => ({
          x: center.x - ((center.x - p.x - dx) / oldScale) * newScale,
          y: center.y - ((center.y - p.y - dy) / oldScale) * newScale,
        }));
        setZoom(newZoom);

        lastTouchRef.current = { dist, center };
      }
    };

    const handleTouchEnd = (e) => {
      touchCountRef.current = e.touches.length;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [zoom, baseScale, dispatch]);

  const [spaceHeld, setSpaceHeld] = useState(false);
  const [panMode, setPanMode] = useState(false); // sticky pan/hand tool
  useEffect(() => {
    const down = (e) => { if (e.code === 'Space' && !e.repeat && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') { e.preventDefault(); setSpaceHeld(true); } };
    const up = (e) => { if (e.code === 'Space') { setSpaceHeld(false); isPanning.current = false; } };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []);


  // --- Eyedropper helper ---
  const sampleColour = useCallback((canvasX, canvasY) => {
    const stage = stageRef.current;
    if (!stage) return;
    // Rasterise the full stage at 1:1 with current scale
    const pixelRatio = 1;
    const dataUrl = stage.toDataURL({ pixelRatio });
    const img = new window.Image();
    img.onload = () => {
      const offscreen = document.createElement('canvas');
      offscreen.width = img.width;
      offscreen.height = img.height;
      const ctx = offscreen.getContext('2d');
      ctx.drawImage(img, 0, 0);
      // Convert canvas coordinates to rasterised pixel coordinates
      // The stage renders at (pan.x, pan.y) with scale, so a canvas point maps to:
      const px = Math.round(canvasX * scale + pan.x);
      const py = Math.round(canvasY * scale + pan.y);
      // Clamp to image bounds
      const cx = Math.max(0, Math.min(px, offscreen.width - 1));
      const cy = Math.max(0, Math.min(py, offscreen.height - 1));
      const pixel = ctx.getImageData(cx, cy, 1, 1).data;
      const hex = '#' + [pixel[0], pixel[1], pixel[2]].map(v => v.toString(16).padStart(2, '0')).join('');
      dispatch({ type: 'SET_COLOUR', payload: hex });
      dispatch({ type: 'ADD_TOAST', payload: { message: `Colour sampled: ${hex}` } });
      // Switch back to pencil after sampling
      dispatch({ type: 'SET_TOOL', payload: 'pencil' });
    };
    img.src = dataUrl;
  }, [scale, pan, stageRef, dispatch]);

  // --- Find stroke at point (for select tool) ---
  const findStrokeAtPoint = useCallback((x, y) => {
    // Hit radius: at least 25 canvas pixels so thin strokes are easy to tap.
    // On a phone at ~0.3x scale, 25 canvas px ≈ 8 screen px — reasonable.
    const MIN_HIT = 25;

    // Simple proximity hit test — check from newest to oldest
    const strokes = allStrokesRef.current;
    for (let i = strokes.length - 1; i >= 0; i--) {
      const s = strokes[i];
      const ox = s.offsetX || 0;
      const oy = s.offsetY || 0;

      if (s.tool === 'text' || s.tool === 'stamp') {
        const sx = ox + s.x;
        const sy = oy + s.y;
        const sz = s.tool === 'stamp' ? Math.max(24, s.size * 3) : (s.fontSize || 24);
        if (x >= sx - sz && x <= sx + sz * 3 && y >= sy - sz && y <= sy + sz) return s;
      }
      if (s.tool === 'shape') {
        const pad = MIN_HIT;
        if (x >= s.x + ox - pad && x <= s.x + ox + (s.width || 0) + pad
         && y >= s.y + oy - pad && y <= s.y + oy + (s.height || 0) + pad) return s;
      }
      if (s.tool === 'gradient' && s.gradientPoints) {
        const [gx1, gy1, gx2, gy2] = s.gradientPoints;
        const gx = Math.min(gx1, gx2);
        const gy = Math.min(gy1, gy2);
        if (x >= gx && x <= gx + Math.abs(gx2 - gx1) && y >= gy && y <= gy + Math.abs(gy2 - gy1)) return s;
      }
      // For freehand strokes, check proximity to any point
      if (s.points && Array.isArray(s.points)) {
        const hitRadius = Math.max(MIN_HIT, s.size + 10);
        for (let j = 0; j < s.points.length; j += 2) {
          const px = s.points[j] + ox;
          const py = s.points[j + 1] + oy;
          if (Math.hypot(x - px, y - py) < hitRadius) return s;
        }
      }
    }
    return null;
  }, [canvas.allStrokes]);

  // --- Pointer handlers ---
  const handlePointerDown = useCallback(async (e) => {
    // Canvas disabled during game phases (spectator, lobby, etc.)
    if (disabled) return;
    // Block drawing during multi-touch (pinch/zoom)
    if (touchCountRef.current >= 2) return;

    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    if (!pos) return;

    if (spaceHeld || panMode || e.evt?.button === 1) {
      isPanning.current = true;
      lastPanPoint.current = { x: pos.x, y: pos.y };
      return;
    }

    const point = getCanvasPoint(stage);
    if (!point) return;

    // --- Eyedropper ---
    if (canvas.tool === 'eyedropper') {
      sampleColour(point[0], point[1]);
      return;
    }

    // --- Text tool ---
    if (canvas.tool === 'text') {
      setTextInput({ x: point[0], y: point[1] });
      return;
    }

    // --- Select / Move ---
    // If strokes are already selected and the pointer is within the bounding
    // box of the selection → drag-move all of them.
    // Otherwise → marquee area select (drag) or single select (tap).
    if (canvas.tool === 'select') {
      const selIds = selectedIdsRef.current;
      const strokes = allStrokesRef.current;
      const hit = findStrokeAtPoint(point[0], point[1]);

      if (hit) {
        // Clicked on a stroke. Three sub-cases:
        const hitIsSelected = selIds.includes(hit.id);

        if (hitIsSelected) {
          // (a) Already selected → drag all selected strokes
          dragStartRef.current = { x: point[0], y: point[1] };
          dragOffsetsRef.current = {};
          for (const id of selIds) {
            const s = strokes.find(st => st.id === id);
            if (s) dragOffsetsRef.current[id] = { x: s.offsetX || 0, y: s.offsetY || 0 };
          }
        } else {
          // (b) Not selected → select it and prepare for drag
          dispatch({ type: 'SET_SELECTED_STROKES', payload: [hit.id] });
          dragStartRef.current = { x: point[0], y: point[1] };
          dragOffsetsRef.current = { [hit.id]: { x: hit.offsetX || 0, y: hit.offsetY || 0 } };
        }
        selectHitRef.current = null;
        isDrawing.current = true;
      } else {
        // Clicked on empty space → marquee or deselect
        selectHitRef.current = null;
        dragStartRef.current = null;
        setMarquee({ startX: point[0], startY: point[1], currentX: point[0], currentY: point[1] });
        isDrawing.current = true;
      }
      return;
    }

    // --- Transform ---
    if (canvas.tool === 'transform') {
      const hit = findStrokeAtPoint(point[0], point[1]);
      if (hit) {
        dispatch({ type: 'SET_SELECTED_STROKES', payload: [hit.id] });
        dragStartRef.current = { x: point[0], y: point[1] };
        isDrawing.current = true;
      } else {
        dispatch({ type: 'CLEAR_SELECTION' });
      }
      return;
    }

    // --- Stamp ---
    if (canvas.tool === 'stamp') {
      const stampData = {
        userId: user.id, tool: 'stamp', stampEmoji: canvas.activeStamp?.emoji || '⭐',
        colour: canvas.colour, size: canvas.size, opacity: canvas.opacity,
        x: point[0], y: point[1], layer: canvas.activeLayer, timestamp: Date.now(),
      };
      const strokeId = await onPushStroke(stampData);
      if (strokeId) dispatch({ type: 'COMMIT_STROKE', payload: { id: strokeId, ...stampData } });
      return;
    }

    // --- Fill ---
    if (canvas.tool === 'fill') {
      // Check if clicking inside a shape — if so, create a filled copy
      const hitShape = [...allStrokesRef.current].reverse().find(s => {
        if (s.tool !== 'shape') return false;
        const sx = s.x, sy = s.y, sw = s.width, sh = s.height;
        if (s.shapeType === 'rect') return point[0] >= sx && point[0] <= sx + sw && point[1] >= sy && point[1] <= sy + sh;
        if (s.shapeType === 'ellipse') {
          const cx = sx + sw / 2, cy = sy + sh / 2;
          const rx = sw / 2, ry = sh / 2;
          if (rx === 0 || ry === 0) return false;
          return ((point[0] - cx) ** 2) / (rx ** 2) + ((point[1] - cy) ** 2) / (ry ** 2) <= 1;
        }
        return false;
      });

      if (hitShape) {
        // Push a filled shape to RTDB (rendered behind the outline)
        const fillData = {
          userId: user.id, tool: 'shape', shapeType: hitShape.shapeType,
          x: hitShape.x, y: hitShape.y, width: hitShape.width, height: hitShape.height,
          colour: 'transparent', fillColour: canvas.colour,
          size: 0, opacity: canvas.opacity,
          layer: hitShape.layer || 'foreground',
          timestamp: hitShape.timestamp - 1, // render just behind the outline
        };
        const strokeId = await onPushStroke(fillData);
        if (strokeId) dispatch({ type: 'COMMIT_STROKE', payload: { id: strokeId, ...fillData } });
        dispatch({ type: 'ADD_TOAST', payload: { message: 'Shape filled' } });
      } else {
        // Fill a rectangle at the click point
        const fillSize = 200;
        const fillData = {
          userId: user.id, tool: 'shape', shapeType: 'rect',
          x: point[0] - fillSize / 2, y: point[1] - fillSize / 2,
          width: fillSize, height: fillSize,
          colour: 'transparent', fillColour: canvas.colour,
          size: 0, opacity: canvas.opacity,
          layer: canvas.activeLayer, timestamp: Date.now(),
        };
        const strokeId = await onPushStroke(fillData);
        if (strokeId) dispatch({ type: 'COMMIT_STROKE', payload: { id: strokeId, ...fillData } });
        dispatch({ type: 'ADD_TOAST', payload: { message: 'Filled area placed — use background toggle for full canvas' } });
      }
      return;
    }

    // --- Gradient fill (drag start→end) ---
    if (canvas.tool === 'gradient') {
      isDrawing.current = true;
      shapeStartRef.current = { x: point[0], y: point[1] };
      setActiveShape({ startX: point[0], startY: point[1], currentX: point[0], currentY: point[1] });
      return;
    }

    // --- Bezier curve (click to add points, double-click to finish) ---
    if (canvas.tool === 'bezier') {
      setBezierPoints((prev) => [...prev, point[0], point[1]]);
      return;
    }

    isDrawing.current = true;

    if (isShapeTool(canvas.tool)) {
      shapeStartRef.current = { x: point[0], y: point[1] };
      setActiveShape({ startX: point[0], startY: point[1], currentX: point[0], currentY: point[1] });
      return;
    }

    pointsRef.current = [point];
    const pathData = getStrokePath([point], canvas.tool, canvas.size);
    dispatch({
      type: 'SET_ACTIVE_STROKE',
      payload: {
        pathData, tool: canvas.tool,
        colour: canvas.tool === 'eraser' ? '#000000' : canvas.colour,
        opacity: canvas.tool === 'eraser' ? 1 : canvas.opacity,
      },
    });
  }, [canvas.tool, canvas.size, canvas.colour, canvas.opacity, canvas.activeStamp, canvas.activeLayer,
      dispatch, getCanvasPoint, user.id, onPushStroke, spaceHeld, panMode, sampleColour, findStrokeAtPoint, disabled]);

  const handlePointerMove = useCallback((e) => {
    // Block during multi-touch
    if (touchCountRef.current >= 2) return;

    const stage = e.target.getStage();

    if (isPanning.current) {
      const pos = stage.getPointerPosition();
      const last = lastPanPoint.current;
      if (pos && last) {
        setPan((prev) => ({
          x: prev.x + (pos.x - last.x),
          y: prev.y + (pos.y - last.y),
        }));
        lastPanPoint.current = { x: pos.x, y: pos.y };
      } else if (pos) {
        // First move after pan started — initialise the reference point
        lastPanPoint.current = { x: pos.x, y: pos.y };
      }
      return;
    }

    const point = getCanvasPoint(stage);
    if (!point) return;

    // Brush preview cursor position
    setCursorPos(point);

    if (onUpdateCursor) {
      onUpdateCursor(point[0], point[1], user.nickname, user.colour, canvas.tool);
    }

    if (!isDrawing.current) return;

    // --- Select: move strokes OR marquee ---
    if (canvas.tool === 'select' && isDrawing.current) {
      if (dragStartRef.current) {
        // Dragging stroke(s) — use the IDs from dragOffsetsRef (set at pointerDown)
        const dragIds = Object.keys(dragOffsetsRef.current);
        if (dragIds.length > 0) {
          const dx = point[0] - dragStartRef.current.x;
          const dy = point[1] - dragStartRef.current.y;
          const updates = dragIds.map((id) => {
            const orig = dragOffsetsRef.current[id] || { x: 0, y: 0 };
            return { id, updates: { offsetX: orig.x + dx, offsetY: orig.y + dy } };
          });
          dispatch({ type: 'UPDATE_STROKES_BATCH', payload: updates });
          return;
        }
      }
      // Marquee selection drag
      if (marqueeRef.current) {
        setMarquee(prev => prev ? { ...prev, currentX: point[0], currentY: point[1] } : null);
        return;
      }
    }

    // --- Transform drag (rotation based on angle from centre) ---
    if (canvas.tool === 'transform' && dragStartRef.current && selectedIdsRef.current.length > 0) {
      const id = selectedIdsRef.current[0];
      const stroke = allStrokesRef.current.find(s => s.id === id);
      if (stroke) {
        const cx = stroke.x + (stroke.offsetX || 0);
        const cy = stroke.y + (stroke.offsetY || 0);
        const angle = Math.atan2(point[1] - cy, point[0] - cx) * (180 / Math.PI);
        const dist = Math.hypot(point[0] - dragStartRef.current.x, point[1] - dragStartRef.current.y);
        const scaleFactor = Math.max(0.1, Math.min(5, 1 + (dist - 50) / 200));
        dispatch({ type: 'UPDATE_STROKE', payload: { id, updates: { rotation: angle, scaleX: scaleFactor, scaleY: scaleFactor } } });
      }
      return;
    }

    // --- Gradient preview ---
    if (canvas.tool === 'gradient') {
      setActiveShape((prev) => prev ? { ...prev, currentX: point[0], currentY: point[1] } : null);
      return;
    }

    if (isShapeTool(canvas.tool)) {
      setActiveShape((prev) => prev ? { ...prev, currentX: point[0], currentY: point[1] } : null);
      return;
    }

    pointsRef.current.push(point);
    const pathData = getStrokePath(pointsRef.current, canvas.tool, canvas.size);
    dispatch({
      type: 'SET_ACTIVE_STROKE',
      payload: {
        pathData, tool: canvas.tool,
        colour: canvas.tool === 'eraser' ? '#000000' : canvas.colour,
        opacity: canvas.tool === 'eraser' ? 1 : canvas.opacity,
      },
    });
  }, [canvas.tool, canvas.size, canvas.colour, canvas.opacity, canvas.selectedStrokeIds,
      dispatch, getCanvasPoint, onUpdateCursor, user.nickname, user.colour, canvas.allStrokes]);

  const handlePointerUp = useCallback(async () => {
    if (isPanning.current) {
      isPanning.current = false;
      lastPanPoint.current = null;
      return;
    }
    if (!isDrawing.current) return;
    isDrawing.current = false;

    // --- Select release ---
    if (canvas.tool === 'select') {
      // If we were dragging strokes, persist their offsets to RTDB
      const strokes = allStrokesRef.current;
      const dragIds = Object.keys(dragOffsetsRef.current);
      if (dragStartRef.current && dragIds.length > 0 && onUpdateStrokeInDB) {
        dragIds.forEach((id) => {
          const stroke = strokes.find(s => s.id === id);
          if (stroke && (stroke.offsetX || stroke.offsetY)) {
            onUpdateStrokeInDB(id, { offsetX: stroke.offsetX || 0, offsetY: stroke.offsetY || 0 });
          }
        });
        dragStartRef.current = null;
        return;
      }

      // Marquee / tap selection
      const mq = marqueeRef.current;
      if (mq) {
        const mx = Math.min(mq.startX, mq.currentX);
        const my = Math.min(mq.startY, mq.currentY);
        const mw = Math.abs(mq.currentX - mq.startX);
        const mh = Math.abs(mq.currentY - mq.startY);

        if (mw > 10 || mh > 10) {
          // Dragged a rectangle — area select all strokes within it
          const selected = allStrokesRef.current.filter(s => {
            let sx = (s.offsetX || 0) + (s.x || 0);
            let sy = (s.offsetY || 0) + (s.y || 0);
            if (s.points && Array.isArray(s.points)) {
              for (let j = 0; j < s.points.length; j += 2) {
                const px = s.points[j] + (s.offsetX || 0);
                const py = s.points[j + 1] + (s.offsetY || 0);
                if (px >= mx && px <= mx + mw && py >= my && py <= my + mh) return true;
              }
              return false;
            }
            if (s.tool === 'shape') {
              const cx = s.x + (s.width || 0) / 2;
              const cy = s.y + (s.height || 0) / 2;
              return cx >= mx && cx <= mx + mw && cy >= my && cy <= my + mh;
            }
            return sx >= mx && sx <= mx + mw && sy >= my && sy <= my + mh;
          }).map(s => s.id);

          if (selected.length > 0) {
            dispatch({ type: 'SET_SELECTED_STROKES', payload: selected });
            dispatch({ type: 'ADD_TOAST', payload: { message: `${selected.length} item${selected.length > 1 ? 's' : ''} selected` } });
          } else {
            dispatch({ type: 'CLEAR_SELECTION' });
          }
        } else {
          // Small movement = tap — select the single stroke under the pointer
          const hit = selectHitRef.current;
          if (hit) {
            dispatch({ type: 'SET_SELECTED_STROKES', payload: [hit.id] });
          } else {
            dispatch({ type: 'CLEAR_SELECTION' });
          }
        }
        setMarquee(null);
        selectHitRef.current = null;
      }
      dragStartRef.current = null;
      return;
    }

    // --- Transform release — persist to RTDB ---
    if (canvas.tool === 'transform') {
      if (selectedIdsRef.current.length > 0 && onUpdateStrokeInDB) {
        selectedIdsRef.current.forEach((id) => {
          const stroke = allStrokesRef.current.find(s => s.id === id);
          if (stroke) {
            onUpdateStrokeInDB(id, {
              rotation: stroke.rotation || 0,
              scaleX: stroke.scaleX || 1,
              scaleY: stroke.scaleY || 1,
            });
          }
        });
      }
      dragStartRef.current = null;
      return;
    }

    // --- Gradient commit ---
    if (canvas.tool === 'gradient' && activeShape) {
      const { startX, startY, currentX, currentY } = activeShape;
      if (Math.hypot(currentX - startX, currentY - startY) < 5) { setActiveShape(null); return; }
      const gradientData = {
        userId: user.id, tool: 'gradient',
        colour: canvas.colour, secondaryColour: canvas.secondaryColour,
        size: 0, opacity: canvas.opacity,
        gradientPoints: [startX, startY, currentX, currentY],
        layer: canvas.activeLayer, timestamp: Date.now(),
      };
      const strokeId = await onPushStroke(gradientData);
      if (strokeId) dispatch({ type: 'COMMIT_STROKE', payload: { id: strokeId, ...gradientData } });
      setActiveShape(null);
      return;
    }

    // --- Shape commit ---
    if (isShapeTool(canvas.tool) && activeShape) {
      const { startX, startY, currentX, currentY } = activeShape;
      const x = Math.min(startX, currentX);
      const y = Math.min(startY, currentY);
      const w = Math.abs(currentX - startX);
      const h = Math.abs(currentY - startY);
      if (canvas.tool === 'line') {
        if (Math.hypot(currentX - startX, currentY - startY) < 5) { setActiveShape(null); return; }
      } else if (w < 5 && h < 5) { setActiveShape(null); return; }
      const shapeData = {
        userId: user.id, tool: 'shape', shapeType: canvas.tool,
        colour: canvas.colour, size: canvas.size, opacity: canvas.opacity,
        x, y, width: w, height: h,
        points: canvas.tool === 'line' ? [startX, startY, currentX, currentY] : null,
        timestamp: Date.now(),
      };
      const strokeId = await onPushStroke(shapeData);
      if (strokeId) dispatch({ type: 'COMMIT_STROKE', payload: { id: strokeId, ...shapeData } });
      setActiveShape(null);
      shapeStartRef.current = null;
      return;
    }

    // --- Freehand commit ---
    const points = pointsRef.current;
    if (points.length === 0) return;
    const pathData = getStrokePath(points, canvas.tool, canvas.size);
    const flatPoints = flattenPoints(points);
    validateStrokeSize(flatPoints);
    const isEraser = canvas.tool === 'eraser';
    const strokeData = {
      userId: user.id, tool: canvas.tool,
      colour: isEraser ? '#000000' : canvas.colour,
      size: canvas.size, opacity: isEraser ? 1 : canvas.opacity,
      layer: canvas.activeLayer, points: flatPoints,
      timestamp: Date.now(),
    };
    // Tag strokes with turnIndex during Relay Mode for playback
    if (state.game.mode === 'relay' && state.game.phase === 'active' && state.game.turnOrder?.length > 0) {
      const posInOrder = state.game.turnOrder.indexOf(state.game.currentDrawerId);
      strokeData.turnIndex = ((state.game.round - 1) * state.game.turnOrder.length) + posInOrder;
    }
    // Tag strokes with zone/creature index during Exquisite Corpse
    if (state.game.mode === 'corpse' && state.game.phase === 'drawing') {
      strokeData.zoneIndex = state.game.currentZone;
      strokeData.creatureIndex = state.game.currentCreature;
    }
    if (canvas.soloMode) {
      const soloId = `solo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      dispatch({ type: 'ADD_SOLO_STROKE', payload: { id: soloId, ...strokeData, pathData } });
      pointsRef.current = [];
      return;
    }
    const strokeId = await onPushStroke(strokeData);
    if (strokeId) {
      dispatch({ type: 'COMMIT_STROKE', payload: { id: strokeId, ...strokeData, pathData } });
    } else {
      dispatch({ type: 'SET_ACTIVE_STROKE', payload: null });
      dispatch({ type: 'ADD_TOAST', payload: { message: 'Couldn\'t save stroke — check connection' } });
    }
    pointsRef.current = [];
  }, [canvas.tool, canvas.size, canvas.colour, canvas.opacity, canvas.soloMode, canvas.activeLayer,
      canvas.secondaryColour, user.id, dispatch, onPushStroke, activeShape, state.game]);

  // --- Double-click: finish bezier OR edit text ---
  const handleDblClick = useCallback(async (e) => {
    // Finish bezier
    if (canvas.tool === 'bezier' && bezierPoints.length >= 4) {
      const bezierData = {
        userId: user.id, tool: 'bezier', bezierPoints: [...bezierPoints],
        colour: canvas.colour, size: canvas.size, opacity: canvas.opacity,
        layer: canvas.activeLayer, timestamp: Date.now(),
      };
      const strokeId = await onPushStroke(bezierData);
      if (strokeId) dispatch({ type: 'COMMIT_STROKE', payload: { id: strokeId, ...bezierData } });
      setBezierPoints([]);
      return;
    }

    // Double-click to edit text (works with any tool)
    const stage = e.target.getStage();
    const point = getCanvasPoint(stage);
    if (!point) return;

    const hitText = [...allStrokesRef.current].reverse().find(s => {
      if (s.tool !== 'text') return false;
      const sx = (s.offsetX || 0) + s.x;
      const sy = (s.offsetY || 0) + s.y;
      const sz = s.fontSize || 24;
      const textWidth = (s.textContent?.length || 1) * sz * 0.6;
      return point[0] >= sx && point[0] <= sx + textWidth && point[1] >= sy - 5 && point[1] <= sy + sz + 5;
    });

    if (hitText) {
      setEditingStrokeId(hitText.id);
      setTextInput({ x: hitText.x, y: hitText.y });
    }
  }, [canvas.tool, bezierPoints, canvas.colour, canvas.size, canvas.opacity, canvas.activeLayer,
      canvas.allStrokes, user.id, dispatch, onPushStroke, getCanvasPoint]);

  // --- Text commit (new or edit) ---
  const commitText = useCallback(async (textOptions) => {
    if (!textInput) return;

    // Editing existing text — update in place and persist to RTDB
    if (editingStrokeId) {
      const textUpdates = {
        textContent: validateTextContent(textOptions.textContent),
        colour: textOptions.colour,
        fontSize: textOptions.fontSize,
        fontFamily: textOptions.fontFamily,
        fontStyle: textOptions.fontStyle,
      };
      dispatch({ type: 'UPDATE_STROKE', payload: { id: editingStrokeId, updates: textUpdates } });
      if (onUpdateStrokeInDB) onUpdateStrokeInDB(editingStrokeId, textUpdates);
      dispatch({ type: 'SET_SELECTED_STROKES', payload: [editingStrokeId] });
      dispatch({ type: 'SET_TOOL', payload: 'select' });
      setTextInput(null);
      setEditingStrokeId(null);
      return;
    }

    // New text
    const textData = {
      userId: user.id, tool: 'text',
      textContent: validateTextContent(textOptions.textContent),
      colour: textOptions.colour,
      fontSize: textOptions.fontSize,
      fontFamily: textOptions.fontFamily,
      fontStyle: textOptions.fontStyle,
      opacity: canvas.opacity,
      x: textOptions.x, y: textOptions.y,
      size: textOptions.fontSize,
      layer: canvas.activeLayer, timestamp: Date.now(),
    };
    const strokeId = await onPushStroke(textData);
    if (strokeId) {
      dispatch({ type: 'COMMIT_STROKE', payload: { id: strokeId, ...textData } });
      // Auto-select and switch to move tool
      dispatch({ type: 'SET_SELECTED_STROKES', payload: [strokeId] });
      dispatch({ type: 'SET_TOOL', payload: 'select' });
      dispatch({ type: 'ADD_TOAST', payload: { message: 'Text placed — drag to reposition' } });
    }
    setTextInput(null);
  }, [textInput, editingStrokeId, canvas.opacity, canvas.activeLayer, user.id, dispatch, onPushStroke]);

  // Compute cursor style
  const getCursorStyle = () => {
    if (disabled) return 'not-allowed';
    if (spaceHeld || panMode) return 'grab';
    return CURSOR_STYLES[canvas.tool] || 'crosshair';
  };

  // Blind relay filter — drawer only sees border-region strokes from other turns
  const isBlindRelay = state.game.mode === 'relay' && state.game.blindRelay
    && state.game.phase === 'active' && state.game.currentDrawerId === user.id;

  const visibleStrokes = isBlindRelay
    ? canvas.allStrokes.filter((s) => {
        // Always show own strokes
        if (s.userId === user.id) return true;
        // For other users' strokes, check if any point is in the border region (outer 15%)
        const borderX = CANVAS_WIDTH * 0.15;  // 300
        const borderY = CANVAS_HEIGHT * 0.15; // 225
        if (s.points && Array.isArray(s.points)) {
          for (let i = 0; i < s.points.length; i += 2) {
            const px = s.points[i] + (s.offsetX || 0);
            const py = s.points[i + 1] + (s.offsetY || 0);
            if (px < borderX || px > CANVAS_WIDTH - borderX || py < borderY || py > CANVAS_HEIGHT - borderY) {
              return true;
            }
          }
          return false;
        }
        // For shapes/stamps/text, check bounding box
        const sx = (s.offsetX || 0) + (s.x || 0);
        const sy = (s.offsetY || 0) + (s.y || 0);
        const sw = s.width || s.fontSize || 40;
        const sh = s.height || s.fontSize || 40;
        return sx < borderX || sx + sw > CANVAS_WIDTH - borderX
          || sy < borderY || sy + sh > CANVAS_HEIGHT - borderY;
      })
    : canvas.allStrokes;

  // Exquisite Corpse zone masking.
  // The DRAWER sees their zone + a small overlap from adjacent zones (for connection hints).
  // NON-DRAWERS see NOTHING — the whole point is that each zone is a surprise.
  // Overlap is 30px — enough for connection lines but not enough to reveal content.
  const isCorpseDrawing = state.game.mode === 'corpse' && state.game.phase === 'drawing';
  const isCorpseDrawer = isCorpseDrawing && state.game.currentDrawerId === user.id;
  const corpseZoneCount = state.game.zoneCount || 3;
  const corpseZoneH = getZoneHeight(corpseZoneCount);
  const OVERLAP_PX = 30;
  const filteredStrokes = isCorpseDrawing
    ? (isCorpseDrawer
      ? visibleStrokes.filter((s) => {
          const zoneIdx = state.game.currentZone ?? 0;
          // Own zone strokes always visible
          if (s.zoneIndex === zoneIdx) return true;
          // Check overlap strip (OVERLAP_PX past zone boundary)
          const zoneTop = zoneIdx * corpseZoneH;
          const zoneBottom = (zoneIdx + 1) * corpseZoneH;
          const viewTop = Math.max(0, zoneTop - OVERLAP_PX);
          const viewBottom = Math.min(CANVAS_HEIGHT, zoneBottom + OVERLAP_PX);
          if (s.points && Array.isArray(s.points)) {
            for (let i = 1; i < s.points.length; i += 2) {
              const py = s.points[i] + (s.offsetY || 0);
              if (py >= viewTop && py <= viewBottom) return true;
            }
            return false;
          }
          const sy = (s.offsetY || 0) + (s.y || 0);
          return sy >= viewTop && sy <= viewBottom;
        })
      : [] // Non-drawers see nothing during corpse drawing
    )
    : visibleStrokes;

  return (
    <div ref={containerRef} className="flex-1 overflow-hidden relative" style={{ cursor: getCursorStyle(), touchAction: 'none' }}>
      {/* Solo mode indicator */}
      {canvas.soloMode && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-violet-600/90 backdrop-blur text-white px-4 py-1.5 rounded-full text-sm font-display">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
          Solo Mode — only you can see your strokes
        </div>
      )}

      {/* Active style indicator */}
      {canvas.activeStyle && (
        <div
          className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 backdrop-blur text-white px-4 py-1.5 rounded-full text-sm font-display shadow-lg"
          style={{ backgroundColor: canvas.activeStyle.colour + 'E6', top: canvas.soloMode ? '44px' : '12px' }}
        >
          <span className="text-base">{canvas.activeStyle.emoji}</span>
          <span>Drawing in <strong>{canvas.activeStyle.name}</strong></span>
          <button
            onClick={() => dispatch({ type: 'CLEAR_ACTIVE_STYLE' })}
            className="ml-1 w-4 h-4 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-[10px] transition-colors"
            title="Clear style"
          >
            ✕
          </button>
        </div>
      )}

      {/* Navigation controls */}
      <div className="absolute bottom-3 left-3 z-10 flex items-center gap-1.5">
        {/* Pan / Hand tool */}
        <button
          onClick={() => setPanMode(!panMode)}
          title={panMode ? 'Exit pan mode' : 'Pan / drag canvas'}
          className={`w-9 h-9 flex items-center justify-center rounded-lg backdrop-blur transition-colors ${
            panMode
              ? 'bg-amber text-white shadow-md'
              : 'bg-charcoal/80 text-white/70 hover:text-white'
          }`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 11V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v1"/>
            <path d="M14 10V4a2 2 0 0 0-2-2 2 2 0 0 0-2 2v6"/>
            <path d="M10 10.5V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v8"/>
            <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/>
          </svg>
        </button>

        {/* Zoom controls */}
        <div className="flex items-center gap-0.5 bg-charcoal/80 backdrop-blur rounded-lg px-1 py-1">
          <button onClick={zoomOut} title="Zoom out" className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-white rounded text-sm font-mono">−</button>
          <button onClick={resetView} title="Reset zoom" className="px-2 h-7 flex items-center justify-center text-white/70 hover:text-white rounded text-xs font-mono min-w-[3rem]">
            {Math.round(zoom * 100)}%
          </button>
          <button onClick={zoomIn} title="Zoom in" className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-white rounded text-sm font-mono">+</button>
        </div>

        {/* Fit to screen */}
        {(zoom !== 1 || pan.x !== 0 || pan.y !== 0) && (
          <button
            onClick={resetView}
            title="Fit to screen"
            className="h-9 px-2.5 flex items-center justify-center gap-1 rounded-lg bg-charcoal/80 backdrop-blur text-white/70 hover:text-white transition-colors text-xs font-display"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/>
            </svg>
            Fit
          </button>
        )}
      </div>

      {/* Text edit modal */}
      {textInput && (
        <TextEditPanel
          position={textInput}
          initialValues={editingStrokeId ? canvas.allStrokes.find(s => s.id === editingStrokeId) : null}
          onCommit={commitText}
          onCancel={() => { setTextInput(null); setEditingStrokeId(null); }}
        />
      )}

      {/* Bezier points preview */}
      {bezierPoints.length >= 2 && canvas.tool === 'bezier' && (
        <div className="absolute top-3 left-3 z-10 bg-charcoal/80 backdrop-blur text-white px-3 py-1 rounded-full text-xs font-display">
          {bezierPoints.length / 2} points — double-click to finish
        </div>
      )}

      <Stage
        ref={stageRef}
        width={dimensions.width}
        height={dimensions.height}
        scaleX={scale}
        scaleY={scale}
        x={pan.x}
        y={pan.y}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onWheel={handleWheel}
        onDblClick={handleDblClick}
        onDblTap={handleDblClick}
      >
        {/* Background */}
        <Layer listening={false}>
          <Rect x={0} y={0} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} fill={canvas.background} />
          {/* Exquisite Corpse zone boundary lines — dynamic based on zone count */}
          {state.game.mode === 'corpse' && state.game.phase === 'drawing' &&
            getZoneBoundaries(state.game.zoneCount || 3).map((y) => (
              <Line key={y} points={[0, y, CANVAS_WIDTH, y]} stroke="#94a3b8" strokeWidth={2} dash={[12, 8]} opacity={0.4} />
            ))
          }
        </Layer>

        {/* Background layer strokes */}
        <Layer listening={false}>
          {filteredStrokes.filter(s => s.layer === 'background').map((stroke) =>
            renderStroke(stroke, null, dimensions, canvas.selectedStrokeIds)
          )}
        </Layer>

        {/* Foreground strokes + active drawing */}
        <Layer listening={false}>
          {/* Clip mask shape outline */}
          {filteredStrokes.filter(s => s.layer !== 'background').map((stroke) =>
            renderStroke(stroke, null, dimensions, canvas.selectedStrokeIds)
          )}

          {/* Active freehand stroke */}
          {canvas.activeStroke && (
            <Path data={canvas.activeStroke.pathData} fill={canvas.activeStroke.colour}
              opacity={canvas.activeStroke.opacity}
              globalCompositeOperation={canvas.activeStroke.tool === 'eraser' ? 'destination-out' : 'source-over'} />
          )}

          {/* Active shape / gradient preview */}
          {activeShape && canvas.tool !== 'gradient' && (
            <ActiveShape shape={activeShape} tool={canvas.tool}
              colour={canvas.colour} opacity={canvas.opacity} size={canvas.size} />
          )}
          {activeShape && canvas.tool === 'gradient' && (
            <Rect
              x={Math.min(activeShape.startX, activeShape.currentX)}
              y={Math.min(activeShape.startY, activeShape.currentY)}
              width={Math.abs(activeShape.currentX - activeShape.startX) || 1}
              height={Math.abs(activeShape.currentY - activeShape.startY) || 1}
              fillLinearGradientStartPoint={{ x: 0, y: 0 }}
              fillLinearGradientEndPoint={{
                x: Math.abs(activeShape.currentX - activeShape.startX) || 1,
                y: Math.abs(activeShape.currentY - activeShape.startY) || 1,
              }}
              fillLinearGradientColorStops={[0, canvas.colour, 1, canvas.secondaryColour]}
              opacity={canvas.opacity} dash={[8, 4]} stroke="#999" strokeWidth={1} />
          )}

          {/* Bezier preview line */}
          {bezierPoints.length >= 2 && (
            <Line points={bezierPoints} stroke={canvas.colour} strokeWidth={canvas.size}
              opacity={0.5} tension={0.5} lineCap="round" dash={[6, 3]} />
          )}

          {/* Marquee selection rectangle */}
          {marquee && (
            <Rect
              x={Math.min(marquee.startX, marquee.currentX)}
              y={Math.min(marquee.startY, marquee.currentY)}
              width={Math.abs(marquee.currentX - marquee.startX)}
              height={Math.abs(marquee.currentY - marquee.startY)}
              stroke="#F59E0B" strokeWidth={1.5 / scale}
              fill="#F59E0B" opacity={0.1}
              dash={[6 / scale, 4 / scale]} />
          )}

          {/* Brush size preview circle at cursor */}
          {cursorPos && !spaceHeld && !panMode && !['select', 'text', 'eyedropper', 'transform'].includes(canvas.tool) && (
            <Circle
              x={cursorPos[0]} y={cursorPos[1]}
              radius={canvas.size / 2}
              stroke={canvas.colour} strokeWidth={1 / scale}
              opacity={0.4} dash={[4 / scale, 4 / scale]} />
          )}
        </Layer>

        {/* Solo mode strokes */}
        {canvas.soloMode && canvas.soloStrokes.length > 0 && (
          <Layer listening={false} opacity={0.85}>
            {canvas.soloStrokes.map((stroke) => (
              <Path key={stroke.id} data={stroke.pathData}
                fill={stroke.colour} opacity={stroke.opacity} />
            ))}
          </Layer>
        )}

        {/* Cursors */}
        <Layer listening={false}>
          {Object.entries(cursors).map(([uid, cursor]) => {
            const toolEmoji = TOOL_LABELS[cursor.tool] || '';
            const label = `${cursor.nickname || ''} ${toolEmoji}`;
            return (
              <Group key={uid} x={cursor.x} y={cursor.y}>
                <Circle radius={6 / scale} fill={cursor.colour || '#888'} opacity={0.8} />
                <Rect x={10 / scale} y={-10 / scale}
                  width={(label.length * 7 + 12) / scale} height={20 / scale}
                  fill={cursor.colour || '#888'} cornerRadius={10 / scale} opacity={0.75} />
                <Text text={label} x={16 / scale} y={-5 / scale}
                  fontSize={11 / scale} fill="#FFFFFF" fontFamily="Space Grotesk" />
              </Group>
            );
          })}
        </Layer>
      </Stage>
    </div>
  );
});

export default CanvasStage;
